import { useState } from 'react'
import { X, Copy, Check, User } from 'lucide-react'

const ROLE_LABELS = { admin: 'Admin', manager: 'Manager', staff: 'Staff' }

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false)
  function handleCopy() {
    navigator.clipboard?.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button
      onClick={handleCopy}
      className="flex items-center space-x-1 text-xs font-medium text-blue-600 hover:text-blue-700 transition"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
      <span>{copied ? 'Copied' : 'Copy'}</span>
    </button>
  )
}

export default function CreateUserModal({
  currentUser,
  organizations,
  branches,
  onAddUser,
  onClose,
}) {
  const isSuperAdmin = currentUser.role === 'superadmin'
  // superadmin can create admins, managers and staff; admins create managers and staff
  const roleOptions = isSuperAdmin ? ['admin', 'manager', 'staff'] : ['manager', 'staff']

  const [step, setStep] = useState('form')
  const [credentials, setCredentials] = useState(null)
  const [formError, setFormError] = useState('')
  const [saving, setSaving] = useState(false)

  const [name, setName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState(isSuperAdmin ? 'admin' : 'manager')
  const [orgId, setOrgId] = useState(isSuperAdmin ? '' : currentUser.organizationId)
  const [branchId, setBranchId] = useState('')

  const availableBranches = branches.filter(
    (b) => b.organizationId === (isSuperAdmin ? orgId : currentUser.organizationId)
  )

  function suggestUsername(fullName) {
    return fullName.trim().toLowerCase().replace(/\s+/g, '').slice(0, 20)
  }

  function handleNameChange(val) {
    setName(val)
    if (!username) setUsername(suggestUsername(val))
  }

  function handleOrgChange(val) {
    setOrgId(val)
    setBranchId('')
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setFormError('')

    if (!name.trim()) { setFormError('Name is required.'); return }
    if (!username.trim()) { setFormError('Username is required.'); return }
    if (!email.trim()) { setFormError('Email is required.'); return }
    if (role === 'admin' && !orgId) { setFormError('Please select an organisation.'); return }
    if (role !== 'admin' && !branchId) { setFormError('Please select a branch.'); return }

    const selectedBranch = branches.find((b) => b.id === branchId)
    const effectiveOrgId =
      role === 'admin' ? orgId : (selectedBranch?.organizationId || currentUser.organizationId)

    setSaving(true)
    const result = await onAddUser({
      name,
      username,
      email,
      role,
      organizationId: effectiveOrgId,
      branchId: role === 'admin' ? null : branchId,
    })
    setSaving(false)

    if (!result.success) {
      setFormError(result.message)
      return
    }

    setCredentials({ username: result.user.username, email, tempPassword: result.tempPassword })
    setStep('success')
  }

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50 animate-fadeIn" onClick={step === 'success' ? undefined : onClose} />

      <div className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-[70] animate-slideUp sm:animate-fadeIn">
        <div
          className="bg-white w-full sm:w-[480px] rounded-t-2xl sm:rounded-2xl flex flex-col shadow-2xl"
          style={{ maxHeight: '92vh' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 flex-shrink-0">
            <h2 className="font-bold text-gray-800">
              {step === 'form' ? 'Create User' : 'Account Created'}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-4 py-4">

            {step === 'form' && (
              <form id="create-user-form" onSubmit={handleSubmit} className="space-y-4">
                {/* Role selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Role</label>
                  <div className={`grid gap-2 ${roleOptions.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {roleOptions.map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => { setRole(r); setBranchId(''); if (isSuperAdmin) setOrgId('') }}
                        className={`py-2.5 rounded-xl text-sm font-semibold border transition ${
                          role === r
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        {ROLE_LABELS[r]}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="e.g. Emeka Okafor"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.trim())}
                    placeholder="e.g. emeka@company.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
                    placeholder="e.g. emekaokafor"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm font-mono"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Display name shown in the app.</p>
                </div>

                {isSuperAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Organisation</label>
                    <select
                      value={orgId}
                      onChange={(e) => handleOrgChange(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm bg-white"
                      required
                    >
                      <option value="">Select organisation</option>
                      {organizations.map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {role !== 'admin' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Branch</label>
                    <select
                      value={branchId}
                      onChange={(e) => setBranchId(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm bg-white"
                      required
                    >
                      <option value="">
                        {availableBranches.length === 0 ? 'Select an organisation first' : 'Select branch'}
                      </option>
                      {availableBranches.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                    {formError}
                  </p>
                )}
              </form>
            )}

            {step === 'success' && credentials && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                    <User className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="font-semibold text-green-800">Account created successfully</p>
                  <p className="text-xs text-green-600 mt-1">
                    Share these credentials with the user. They will be asked to change their password on first login.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-2xl divide-y divide-gray-200 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3.5">
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Email</p>
                      <p className="font-mono font-semibold text-gray-800">{credentials.email}</p>
                    </div>
                    <CopyButton text={credentials.email} />
                  </div>
                  <div className="flex items-center justify-between px-4 py-3.5">
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Temporary Password</p>
                      <p className="font-mono font-semibold text-gray-800 tracking-wider">{credentials.tempPassword}</p>
                    </div>
                    <CopyButton text={credentials.tempPassword} />
                  </div>
                </div>

                <div className="bg-amber-50 rounded-xl px-4 py-3">
                  <p className="text-xs text-amber-700">
                    The user will be prompted to set a new password when they first log in.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="px-4 py-3 border-t border-gray-100 flex-shrink-0"
            style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
          >
            {step === 'form' && (
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="create-user-form"
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition"
                >
                  {saving ? 'Creating…' : 'Create Account'}
                </button>
              </div>
            )}
            {step === 'success' && (
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
